import type { components } from "@oathera/contract/api";
import type { Measure, StatusLevel } from "../../components/index.ts";

/**
 * Presentation vocabulary and the URL⇄filter model for the Events explorer (task 11.12; design
 * 12.2 Events row, 12.3 principles, 8.2 live-tail pause). It keeps, in one testable place:
 *
 * - the ten STANDING filters design 12.2 names (time, component, event name, severity, agent,
 *   decision, reason, operation, gateway, trace) and how they map onto the generated contract query
 *   params of `GET /events` — SENT to the server;
 * - the OTHER screens' drill params (`route`, `outcome`, `category`, `status`, `stage`, `action`,
 *   `path`, `code`, `namespace`, `source_id`, `instance_id`, `risk_tier`, `event_id`, `change`,
 *   `tier`) that the `/events` contract does NOT accept as first-class query params — applied
 *   CLIENT-SIDE over the returned/live events as visible chips, never silently dropped and never
 *   sent to the server as an unsupported param (which the contract would 400);
 * - the exact/estimate `Measure` mapping for the sample-rate-aware `weighted_count` (design 12.3).
 *
 * It defines no API shapes; those come from `@oathera/contract/api`. The URL is the single source of
 * truth for filters (so a drill from any screen is a link and the browser back button works).
 */

/* ---- Contract-derived types ----------------------------------------------------------------- */

type Schemas = components["schemas"];

/** A single redacted event (the row and the detail body). */
export type EventRow = Schemas["Event"];
/** One page of events (items + sample-rate-aware weighted_count + meta). */
export type EventPage = Schemas["EventPage"];
/** A number that says whether it is exact or an estimate. */
export type Quantity = Schemas["Quantity"];
/** The frozen service / severity / decision / boundary-reason enums the filter controls offer. */
export type Service = Schemas["service"];
export type Severity = Schemas["severity"];
export type Decision = Schemas["enum_decision"];
export type BoundaryReason = Schemas["enum_boundary_reason"];

/* ---- The enum option sets the filter controls present (straight from the contract) ---------- */

/** The services (components) a `component` filter chip can carry. */
export const SERVICE_OPTIONS: readonly Service[] = [
  "idp",
  "ais",
  "vci",
  "resource",
  "gateway-mcp",
  "identity-mcp",
  "sim",
];

/** The event severities a `severity` filter chip can carry. */
export const SEVERITY_OPTIONS: readonly Severity[] = ["info", "warn", "error", "critical"];

/** The boundary decisions a `decision` filter chip can carry. */
export const DECISION_OPTIONS: readonly Decision[] = ["allow", "deny"];

/** The boundary reasons a `reason` filter chip can carry (the frozen set). */
export const BOUNDARY_REASON_OPTIONS: readonly BoundaryReason[] = [
  "allowed",
  "operational_boundary_exceeded",
  "capability_not_granted",
  "tier_insufficient",
  "delegation_invalid",
  "task_context_invalid",
  "credentials_invalid",
  "agent_revoked",
  "agent_suspended",
  "principal_inactive",
  "replay_detected",
  "tool_not_configured",
  "decision_unavailable",
  "decision_unverifiable",
  "decision_principal_mismatch",
  "token_invalid",
  "token_expired",
  "unknown_agent",
  "identity_revoked",
  "agent_deprovisioned",
  "principal_unverified",
  "identity_owner_deprovisioned",
  "credential_suspended",
  "credential_revoked",
  "credential_invalid",
  "request_mismatch",
  "gateway_unauthenticated",
  "gateway_not_approved",
  "operation_not_configured",
  "other",
];

/* ---- The resolved filter model -------------------------------------------------------------- */

/**
 * The STANDING filters design names, resolved from the URL. Arrays are the contract's list-valued
 * filters (component/event/severity, matched as "any of"); the rest are single-valued. `time` is
 * NOT held here — it comes from the shared time-range (`from`/`to`), so pausing freezes the window
 * exactly as it does for the other windowed screens.
 */
export interface StandingFilters {
  readonly component: readonly Service[];
  readonly event: readonly string[];
  readonly severity: readonly Severity[];
  readonly agent: string | null;
  readonly decision: Decision | null;
  readonly reason: BoundaryReason | null;
  readonly operation: string | null;
  readonly gateway: string | null;
  readonly trace: string | null;
}

/**
 * A drill param another screen emitted that the `/events` contract does NOT accept as a first-class
 * query param. It is applied CLIENT-SIDE (over the returned page and the live tail) and shown as a
 * visible, clearable chip, so a drill from any screen lands with its intent visible and honest
 * rather than silently dropped — and no unsupported param is ever sent to the server.
 */
export interface AppliedChip {
  readonly key: string;
  readonly value: string;
}

/** The whole filter state resolved from the URL: the named standing filters plus the drill chips. */
export interface EventFilters {
  readonly standing: StandingFilters;
  readonly chips: readonly AppliedChip[];
}

/** The query-string keys the ten standing filters live under (the contract's param names). */
const STANDING_KEYS = {
  component: "component",
  event: "event",
  severity: "severity",
  agent: "agent",
  decision: "decision",
  reason: "reason",
  operation: "operation",
  gateway: "gateway",
  trace: "trace",
} as const;

/**
 * The complete set of URL keys that are NOT standing filters and NOT reserved for the server
 * cursor/order — i.e. the drill params the other screens emit. These are applied client-side as
 * chips. Enumerated (not inferred) so the reader is explicit about what each screen sends:
 *
 * - Service drill  → `route`, `outcome`
 * - Decisions      → server `drill` arbitrary keys (mostly `tier`, `operation`, `gateway`; the ones
 *                    that overlap a standing filter are promoted — see below)
 * - Identity       → `category`, `stage`, `status`, `outcome`, `action`
 * - Enforcement    → `category`, `path`, `code`, `namespace`, `stage`
 * - Integrity      → `category`, `source_id`, `instance_id`
 * - Posture        → `category`, `code`, `risk_tier`, `event_id`, `source_id`, `change`, `tier`
 *
 * `component`, `trace`, `agent`, `decision`, `reason`, `operation`, `gateway`, `event`, `severity`
 * are promoted to the FIRST-CLASS standing filters (they ARE contract params); anything else here
 * is a chip. `from`/`to`/`cursor`/`order`/`limit` are reserved (owned by the window and paging).
 */
const RESERVED_KEYS: ReadonlySet<string> = new Set(["from", "to", "cursor", "order", "limit"]);

const STANDING_KEY_SET: ReadonlySet<string> = new Set(Object.values(STANDING_KEYS));

/** List-valued standing filters are comma-separated in the URL (contract `style: form, explode: false`). */
function splitList(value: string | null): readonly string[] {
  if (value === null) return [];
  return value
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/* ---- URL → filters -------------------------------------------------------------------------- */

/**
 * Resolve the full filter state from the URL search params. The named standing filters are read
 * into {@link StandingFilters} (list filters split on comma, enum filters validated against their
 * frozen option set so a junk value in a hand-edited URL is ignored rather than sent). Every other
 * param that is not reserved becomes an {@link AppliedChip} — including a drill param that overlaps
 * NOTHING in the contract — so it is visible and clearable, and the screen filters the events by it
 * client-side.
 */
export function readFilters(params: URLSearchParams): EventFilters {
  const standing: StandingFilters = {
    component: splitList(params.get(STANDING_KEYS.component)).filter(isService),
    event: splitList(params.get(STANDING_KEYS.event)),
    severity: splitList(params.get(STANDING_KEYS.severity)).filter(isSeverity),
    agent: params.get(STANDING_KEYS.agent),
    decision: asEnum(params.get(STANDING_KEYS.decision), DECISION_OPTIONS),
    reason: asEnum(params.get(STANDING_KEYS.reason), BOUNDARY_REASON_OPTIONS),
    operation: params.get(STANDING_KEYS.operation),
    gateway: params.get(STANDING_KEYS.gateway),
    trace: params.get(STANDING_KEYS.trace),
  };

  const chips: AppliedChip[] = [];
  for (const [key, value] of params.entries()) {
    if (value.length === 0) continue;
    if (RESERVED_KEYS.has(key)) continue;
    if (STANDING_KEY_SET.has(key)) continue;
    chips.push({ key, value });
  }

  return { standing, chips };
}

/* ---- filters → server query (ONLY contract-accepted params) --------------------------------- */

/**
 * Build the query object for `GET /events` from the standing filters. ONLY the params the contract's
 * `/events` operation accepts are included (design 12.2 / the operation's `parameters`): the list
 * filters are re-joined with commas, the single filters passed through. The drill CHIPS are
 * deliberately NOT sent — the contract would 400 an unknown query param — they are applied
 * client-side instead (see {@link eventMatchesChips}). `from`/`to`/`cursor`/`order`/`limit` are
 * added by the hook from the window and paging state, not here.
 */
export function toServerQuery(
  standing: StandingFilters,
): Readonly<Record<string, string | undefined>> {
  const q: Record<string, string | undefined> = {};
  if (standing.component.length > 0) q["component"] = standing.component.join(",");
  if (standing.event.length > 0) q["event"] = standing.event.join(",");
  if (standing.severity.length > 0) q["severity"] = standing.severity.join(",");
  if (standing.agent !== null) q["agent"] = standing.agent;
  if (standing.decision !== null) q["decision"] = standing.decision;
  if (standing.reason !== null) q["reason"] = standing.reason;
  if (standing.operation !== null) q["operation"] = standing.operation;
  if (standing.gateway !== null) q["gateway"] = standing.gateway;
  if (standing.trace !== null) q["trace"] = standing.trace;
  return q;
}

/**
 * A stable key fragment for the standing filters, so a TanStack Query key changes exactly when a
 * server-affecting filter changes (and NOT when only a client-side chip changes — chips re-filter in
 * memory without a refetch).
 */
export function standingKey(standing: StandingFilters): string {
  return JSON.stringify([
    [...standing.component].sort(),
    [...standing.event].sort(),
    [...standing.severity].sort(),
    standing.agent,
    standing.decision,
    standing.reason,
    standing.operation,
    standing.gateway,
    standing.trace,
  ]);
}

/* ---- client-side chip matching -------------------------------------------------------------- */

/**
 * Whether an event satisfies ALL the applied drill chips. A chip is matched against the event's own
 * fields when the chip key names one the contract carries (`source_id`, `instance_id`, `operation`,
 * `gateway` → `gateway_id`, `trace` → `trace_id`, `agent` → `agent_sub`, `decision`, `reason`,
 * `service`), otherwise against the event's redacted `attrs` (a drill like `tier=elevated`,
 * `risk_tier=high`, `code=…`, `stage=…` is a catalog attribute). A chip whose field is absent on the
 * event does NOT match, so a client-side drill narrows honestly rather than widening. Chips combine
 * with AND, matching the contract's own "filters combine with AND" rule.
 */
export function eventMatchesChips(event: EventRow, chips: readonly AppliedChip[]): boolean {
  for (const chip of chips) {
    if (!eventMatchesChip(event, chip)) return false;
  }
  return true;
}

function eventMatchesChip(event: EventRow, chip: AppliedChip): boolean {
  const { key, value } = chip;
  switch (key) {
    case "source_id":
      return event.source_id === value;
    case "instance_id":
      return event.instance_id === value;
    case "operation":
      return event.operation === value;
    case "gateway":
      return event.gateway_id === value;
    case "trace":
      return event.trace_id === value;
    case "agent":
      return event.agent_sub === value;
    case "decision":
      return event.decision === value;
    case "reason":
      return event.reason === value;
    case "service":
    case "component":
      return event.service === value;
    case "name":
    case "event":
      return event.name === value;
    case "event_id":
      return event.id === value;
    default:
      // A drill param the event does not carry as a top-level field is matched against the catalog
      // attributes (`tier`, `risk_tier`, `code`, `stage`, `path`, `namespace`, `change`, `route`,
      // `outcome`, `category`, `status`, `action`, …). The attr value is stringified for comparison.
      return matchesAttr(event, key, value);
  }
}

function matchesAttr(event: EventRow, key: string, value: string): boolean {
  const attrs = event.attrs as Record<string, string | number | boolean> | undefined;
  if (attrs === undefined) return false;
  const got = attrs[key];
  if (got === undefined) return false;
  return String(got) === value;
}

/* ---- Human labels for the drill chips ------------------------------------------------------- */

/** A readable label for a drill-chip key (so a chip reads "Risk tier: high", not "risk_tier: high"). */
export function chipKeyLabel(key: string): string {
  switch (key) {
    case "source_id":
      return "Source";
    case "instance_id":
      return "Instance";
    case "risk_tier":
      return "Risk tier";
    case "event_id":
      return "Event id";
    case "namespace":
      return "Namespace";
    case "category":
      return "Category";
    case "outcome":
      return "Outcome";
    case "status":
      return "Status";
    case "stage":
      return "Stage";
    case "action":
      return "Action";
    case "path":
      return "Path";
    case "code":
      return "Code";
    case "route":
      return "Route";
    case "tier":
      return "Tier";
    case "change":
      return "Change";
    default:
      return key.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
  }
}

/* ---- Exact / estimate for the sample-rate-aware count --------------------------------------- */

/**
 * Map the page's `weighted_count` `Quantity` onto the shared exact/estimate vocabulary (design
 * 12.3). A sampled weighted total is an ESTIMATE and says "est."; a null value cannot be verified
 * (never green). This is the only count the Events page carries, and the contract marks it
 * `estimated` whenever sampling is in effect (FR-9.2).
 */
export function countMeasure(q: Quantity): Measure {
  if (q.value === null) return "unverifiable";
  return q.exactness === "estimated" ? "estimate" : "exact";
}

/** The human label for an event severity, used by the row and the severity badge. */
export function severityLabel(severity: Severity): string {
  switch (severity) {
    case "info":
      return "Info";
    case "warn":
      return "Warning";
    case "error":
      return "Error";
    case "critical":
      return "Critical";
  }
}

/**
 * The status level for a severity (icon + text, never colour alone; design 12.4). `info` is neutral;
 * `warn` warns; `error`/`critical` are errors. Severity is NOT a health state, so it maps onto the
 * shared levels only to pick a consistent icon/affordance, never to paint an event green.
 */
export function severityLevel(severity: Severity): StatusLevel {
  switch (severity) {
    case "info":
      return "neutral";
    case "warn":
      return "warn";
    case "error":
    case "critical":
      return "error";
  }
}

/** The status level for a boundary decision: a deny is a warning the operator should see. */
export function decisionLevel(decision: Decision): StatusLevel {
  return decision === "deny" ? "warn" : "neutral";
}

/* ---- Emptiness ------------------------------------------------------------------------------ */

/**
 * True when there is genuinely nothing to show in the explorer: no live-tail events (after the
 * client-side chips) AND the paged query returned no items. The screen distinguishes this from
 * "loading" and from "a filter is too narrow" in its empty cause.
 */
export function isEventsEmpty(liveCount: number, pagedCount: number): boolean {
  return liveCount === 0 && pagedCount === 0;
}

/* ---- Links ---------------------------------------------------------------------------------- */

/** The detail route for one event id (self-link / deep-link to a single event). */
export function eventDetailHref(id: string): string {
  return `/events/${encodeURIComponent(id)}`;
}

/** The trace detail route for an event that carries a trace (design: link to its trace). */
export function traceHref(traceId: string): string {
  return `/traces/${encodeURIComponent(traceId)}`;
}

/* ---- validators ----------------------------------------------------------------------------- */

function isService(value: string): value is Service {
  return (SERVICE_OPTIONS as readonly string[]).includes(value);
}

function isSeverity(value: string): value is Severity {
  return (SEVERITY_OPTIONS as readonly string[]).includes(value);
}

function asEnum<T extends string>(value: string | null, options: readonly T[]): T | null {
  if (value === null) return null;
  return (options as readonly string[]).includes(value) ? (value as T) : null;
}
